import React from "react";
import { connect } from "react-redux";
import { ISchemaKey } from "helpers/product-collection";
import { getDoorClassificationSchema } from "reducers/selectors/collection";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { IStore } from "reducers";
import { Popover } from "material-ui";
import { FiltersIcon } from "./filters-icon";
import { CollectionRefs } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/dimensions-tool-panel";
import { FiltersContent } from "./filter-content";
import { IFilters } from "../types";
import { transformQueryBuilderRulesToAttributeFilters, transformQueryBuilderRulesToDimensionFilters } from "../utils";
import Chip from "@material-ui/core/Chip";
import makeStyles from "@material-ui/core/styles/makeStyles";
import theme from "styles/theme";
import classnames from "classnames";

interface IOwnProps {
    allocationDimensions: IAllocationDimension[];
    appliedFilters: IFilters;
    onFiltersChange: (filters: IFilters) => void;
    isServerRollup: boolean;
    collectionId?: string;
}
interface IStateProps {
    allocationDimensionAttributes: ISchemaKey[];
    collectionsRefs: CollectionRefs;
}
type IProps = IOwnProps & IStateProps;

const useStyles = makeStyles({
    filterRoot: {
        display: "flex",
        alignItems: "center",
        height: "25px",
    },
    clearButton: {
        color: theme.palette.darkGrey,
        backgroundColor: theme.palette.borderColor,
        height: "25px",
        textTransform: "none",
        fontSize: "14px",
        overflow: "hidden",
        minWidth: 65,
    },
    filterIcon: {
        color: theme.palette.accent4Color + " !important",
        width: "14px !important",
        height: "14px !important",
    },
});

const FiltersView: React.FC<IProps> = ({
    allocationDimensions,
    appliedFilters,
    allocationDimensionAttributes,
    collectionsRefs,
    onFiltersChange,
    isServerRollup,
    collectionId,
}) => {
    const classes = useStyles();
    const [isMenuOpen, setIsMenuOpen] = React.useState<React.ReactInstance>();

    const handleOpenMenu = React.useCallback((event) => {
        setIsMenuOpen(event.currentTarget);
    }, []);

    const handleCloseMenu = React.useCallback(() => {
        setIsMenuOpen(null);
    }, []);

    const filtersApplied = React.useMemo(() => {
        return (
            (appliedFilters.dimensionFilters || []).reduce(
                (acc, dimension) => acc + (dimension && dimension.length !== 0 ? 1 : 0),
                0
            ) + Object.keys(appliedFilters.attributeFilters || {}).length
        );
    }, [appliedFilters]);

    const handleFilterChanged = React.useCallback(
        (filters?: IFilters) => {
            onFiltersChange(
                filters
                    ? filters
                    : {
                          dimensionFilters: transformQueryBuilderRulesToDimensionFilters(allocationDimensions, []),
                          attributeFilters: transformQueryBuilderRulesToAttributeFilters([]),
                      }
            );
            handleCloseMenu();
        },
        [onFiltersChange, allocationDimensions, handleCloseMenu]
    );

    const handleResetDimensionFilters = React.useCallback((): void => {
        handleFilterChanged();
    }, [handleFilterChanged]);

    const chipClasses = React.useMemo(() => ({ label: "chipLabel" }), []);

    const chipIcon = React.useMemo(
        (): JSX.Element => <FiltersIcon hasFilters={filtersApplied > 0} />,
        [filtersApplied]
    );

    return (
        <>
            <div className={classnames(classes.filterRoot)} onClick={handleOpenMenu}>
                {filtersApplied === 0 && <FiltersIcon hasFilters={filtersApplied > 0} />}
                {filtersApplied > 0 && (
                    <Chip
                        className={classnames(classes.clearButton)}
                        classes={chipClasses}
                        label={filtersApplied}
                        icon={chipIcon}
                        onDelete={handleResetDimensionFilters}
                    />
                )}
            </div>
            <Popover
                open={!!isMenuOpen}
                anchorEl={isMenuOpen}
                onRequestClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                style={{
                    marginTop: "3px",
                }}
            >
                <FiltersContent
                    allocationDimensions={allocationDimensions}
                    allocationDimensionAttributes={allocationDimensionAttributes}
                    appliedFilters={appliedFilters}
                    collectionsRefs={collectionsRefs}
                    onFiltersChange={handleFilterChanged}
                    onRequestClose={handleCloseMenu}
                    isServerRollup={isServerRollup}
                    collectionId={collectionId}
                />
            </Popover>
        </>
    );
};

const mapStateToProps = (state: IStore): IStateProps => {
    const {
        collection: { collectionId, collectionsRefs },
    } = state;
    const doorClassificationSchema = getDoorClassificationSchema(state, collectionId);

    return {
        allocationDimensionAttributes: doorClassificationSchema,
        collectionsRefs,
    };
};

export const FiltersContainer = connect<IStateProps, {}, IOwnProps>(mapStateToProps)(FiltersView);
export default FiltersContainer;
